import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

import {
  Dialog,
  DialogContent,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const QR_TTL = 20; // seconds — wwebjs rotaciona QR a cada ~20s

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const [qrCode, setQrCode] = useState("");
  const [countdown, setCountdown] = useState(QR_TTL);
  const countdownRef = useRef(null);
  const qrReceivedAt = useRef(null);

  const startCountdown = (secondsRemaining = QR_TTL) => {
    clearInterval(countdownRef.current);
    setCountdown(secondsRemaining);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;
      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        if (data.qrcode) {
          setQrCode(data.qrcode);
          // Se o QR já está expirado (elapsed >= TTL), mostra "aguardando" e
          // espera o próximo via socket — não inicia contagem regressiva artificial
          const elapsed = data.updatedAt
            ? Math.floor((Date.now() - new Date(data.updatedAt).getTime()) / 1000)
            : 0;
          const remaining = QR_TTL - elapsed;
          qrReceivedAt.current = Date.now();
          if (remaining > 0) {
            startCountdown(remaining);
          } else {
            setCountdown(0);
          }
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
    return () => clearInterval(countdownRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsAppId]);

  useEffect(() => {
    if (!whatsAppId) return;
    const socket = openSocket();

    socket.on("whatsappSession", (data) => {
      if (data.action === "update" && String(data.session.id) === String(whatsAppId)) {
        // Só é sucesso de fato quando o status vira CONNECTED — a ausência de
        // qrcode sozinha NÃO garante conexão (também fica vazia em FAILED e
        // OPENING durante reconexão, o que fechava o modal com um falso
        // "conectado com sucesso").
        if (data.session.status === "CONNECTED") {
          clearInterval(countdownRef.current);
          toast.success("WhatsApp conectado com sucesso!");
          onClose();
        } else if (data.session.qrcode) {
          setQrCode(data.session.qrcode);
          qrReceivedAt.current = Date.now();
          startCountdown(QR_TTL);
        } else if (data.session.status === "FAILED") {
          clearInterval(countdownRef.current);
          setQrCode("");
          toast.error("Falha ao conectar — tente gerar um novo QR Code.");
        }
      }
    });

    return () => {
      socket.disconnect();
      clearInterval(countdownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsAppId, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" scroll="paper">
      <DialogContent>
        <Paper elevation={0}>
          <Typography color="primary" gutterBottom>
            {i18n.t("qrCode.message")}
          </Typography>
          {qrCode ? (
            <>
              <QRCodeSVG
                value={qrCode.startsWith("https://") ? qrCode : `https://wa.me/settings/linked_devices#${qrCode}`}
                size={300}
                marginSize={4}
              />
              <Typography
                variant="caption"
                display="block"
                align="center"
                style={{ marginTop: 8, color: countdown <= 5 ? "#E74C3C" : "#8A9096" }}
              >
                {countdown > 0
                  ? `QR expira em ${countdown}s`
                  : "Aguardando novo QR Code..."}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(countdown / QR_TTL) * 100}
                style={{ marginTop: 4, height: 4, borderRadius: 2 }}
                color={countdown <= 5 ? "secondary" : "primary"}
              />
            </>
          ) : (
            <span>Waiting for QR Code</span>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
